import Grid from '@mui/material/Grid';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Typography from '@mui/material/Typography';
import * as _ from 'lodash';
import { useEffect } from 'react';
import { CommonButton } from 'gemlux-common-components';
import { Box } from '@mui/material';
import PersonalCredential from './PersonalCredential';
import { isPhoneNumberValid, validation } from '../../../schema/validation';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { authActions } from '../../../redux/auth/authSlice';
import BusinessCredential from './BusinessCredential';
import { AnswerType } from '../../../utils/enums/answer-type.enum';
import { RootState } from '../../../redux/store';
import LogoSVG from '../../../assets/img/sidebar/logo.svg';
import IconButtonLeft from '../../../assets/img/icon-btn-left.svg';
import IconButtonRight from '../../../assets/img/icon-btn-right.svg';
import LogoLink from '../../LogoLink/LogoLink';

const validationSchemas = [
  yup.object({
    username: validation.username,
    firstName: validation.firstName,
    familyName: validation.familyName,
    businessEmail: validation.email,
    confirmEmail: yup
      .string()
      .oneOf([yup.ref('businessEmail')], 'Email Address do not match!')
      .required('Confirm Email is required!'),
    phoneNumber: yup
      .string()
      .required('Mobile Number is required!')
      .test('phoneNumber', 'Invalid Mobile Number!', isPhoneNumberValid),
    password: validation.password,
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Password doesn’t match!')
      .required('Confirm Password is required!'),
  }),
  yup.object({
    companyName: validation.companyName,
    country: validation.country,
    designationId: yup.string().required('Your Role in the Company is required!'),
    companyWebsite: validation.companyWebsite,
    isAgreedToTermsAndCondition: validation.isAgreedToTermsAndCondition,
    customDesignation: yup.string().when('designationId', {
      is: '7',
      then: (schema) => schema.required('Your Role in the Company is required!'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  }),
];

type ICredentialProps = {
  setActiveStep: (_activeStep: number) => void;
  activeStep: number;
  title: string;
};

function Credential({ setActiveStep, activeStep, title }: ICredentialProps) {
  const dispatch = useAppDispatch();
  const {
    credentials = {
      // Default values in case credentials is undefined
      username: '',
      firstName: '',
      familyName: '',
      businessEmail: '',
      confirmEmail: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      companyName: '',
      country: '',
      designationId: '',
      customDesignation: '',
      companyWebsite: '',
      isAgreedToTermsAndCondition: false,
    },
    componentStatus = {},
    signUpData = { plan: '', roles: [], questionOneAnswer: '', questionTwoAnswer: '' },
  } = useAppSelector((state: RootState) => state.auth) || {};
  const isSignUpSuccess = _.get(componentStatus, 'isSignUpSuccess', undefined);
  const isSubmitting = _.get(componentStatus, 'isSubmitting', false) as boolean;
  const isUsernameAlreadyExists = _.get(componentStatus, 'isUsernameAlreadyExists', undefined) as boolean;
  const isEmailAlreadyExists = _.get(componentStatus, 'isEmailAlreadyExists', undefined) as boolean;

  useEffect(() => {
    if (activeStep === 6 && isSignUpSuccess) {
      setActiveStep(activeStep + 1);
      dispatch(authActions.setComponentStatus({ isSignUpSuccess: undefined }));
    }
  }, [isSignUpSuccess, activeStep]);

  useEffect(
    () => () => {
      dispatch(authActions.setComponentStatus({ error: '' }));
    },
    [],
  );

  const formik = useFormik({
    initialValues: credentials,
    validationSchema: validationSchemas[activeStep - 5],
    enableReinitialize: true, // Ensures formik updates if credentials change
    onSubmit: ({
      username,
      firstName,
      familyName,
      businessEmail,
      confirmEmail,
      phoneNumber,
      password,
      confirmPassword,
      companyName,
      country,
      designationId,
      companyWebsite,
      isAgreedToTermsAndCondition,
    }) => {
      dispatch(
        authActions.setCredentials({
          username,
          firstName,
          familyName,
          businessEmail,
          confirmEmail,
          phoneNumber,
          password,
          confirmPassword,
          companyName,
          country,
          designationId,
          customDesignation: formik.values.customDesignation,
          companyWebsite,
          isAgreedToTermsAndCondition,
        }),
      );
      if (activeStep === 6 && signUpData.plan && signUpData.roles) {
        dispatch(authActions.setComponentStatus({ isSubmitting: true, error: undefined }));
        dispatch(
          authActions.signUpAsync({
            username,
            firstName,
            familyName,
            email: businessEmail,
            phone: phoneNumber,
            password,
            designationId: Number(designationId),
            ...(formik.values.customDesignation && { customDesignation: formik.values.customDesignation }),
            businessRole: signUpData.roles,
            organization: {
              companyName,
              country,
              website: companyWebsite,
              ownDevice: signUpData.questionOneAnswer === AnswerType.YES,
              thirdPartyDevice: signUpData.questionTwoAnswer === AnswerType.YES,
              subscriptionPlan: signUpData.plan,
            },
          }),
        );
      }
    },
  });

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleNext = () => {
    formik.handleSubmit();
    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        if (activeStep === 5 && !isUsernameAlreadyExists && !isEmailAlreadyExists) {
          setActiveStep(activeStep + 1);
        }
      }
    });
  };

  const enterKeyDownHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      // Avoid triggering when user is typing inside an input or textarea
      formik.handleSubmit(); // Directly trigger form submission
      formik.validateForm().then((errors) => {
        if (Object.keys(errors).length === 0) {
          if (activeStep === 5 && !isUsernameAlreadyExists && !isEmailAlreadyExists) {
            setActiveStep(activeStep + 1);
          }
          if (activeStep === 6) {
            setActiveStep(activeStep + 1);
          }
        }
      });
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', enterKeyDownHandler);
    return () => {
      document.removeEventListener('keydown', enterKeyDownHandler);
    };
  }, []);

  return (
    <Grid
      display="flex"
      flexDirection="column"
      gap={3}
      sx={{
        maxWidth: '728px',
        backgroundColor: '#FFFFFF',
        padding: '5%',
        borderRadius: '8px',
        border: '1px solid #DCE0E5',
        boxShadow: '0px 1px 2px rgba(128, 128, 128, 0.12)',
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap="10px" padding="3%">
        <LogoLink width="173.343px" height="32px" />
      </Box>
      <Typography variant="title5Bold" textAlign="center">
        {title}
      </Typography>
      {activeStep === 5 && <PersonalCredential formik={formik} />}
      {activeStep === 6 && <BusinessCredential formik={formik} />}
      <Grid display="flex" justifyContent="space-between">
        <CommonButton
          variant="outlined"
          padding="12px 32px"
          textColor="#3348FF"
          borderColor="#3348FF"
          onClick={handleBack}
          startIcon={<Box component="img" src={IconButtonLeft} />}
        >
          Go Previous
        </CommonButton>
        <CommonButton
          variant="contained"
          padding="12px 32px"
          backgroundColor="#3348FF"
          onClick={handleNext}
          endIcon={<Box component="img" src={IconButtonRight} />}
          loading={isSubmitting}
        >
          Go Next
        </CommonButton>
      </Grid>
      {formik.errors && typeof formik.errors === 'string' && (
        <Typography variant="textSM" color="#DF0C3D" textAlign="center">
          {formik.errors}
        </Typography>
      )}
      {componentStatus.error && (
        <Typography variant="textSM" color="#DF0C3D" textAlign="center">
          {typeof componentStatus.error === 'string' && _.capitalize(componentStatus.error)}
        </Typography>
      )}
    </Grid>
  );
}

export default Credential;
